import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KeyRound, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { userService } from '../services/user-service';

/**
 * Restablecer la contraseña de otra persona, siendo admin.
 *
 * 🔴 Esto no existía, y su ausencia costó una intervención a mano en la base de
 * producción: el 12-ago-2026 hubo que cambiar una contraseña por SSH con un
 * UPDATE porque la aplicación no tenía la función (ver `vaultProgress/130`).
 *
 * Dos cosas que el diálogo tiene que decir y no callar:
 *
 *  1. **No cierra las sesiones abiertas.** El token de acceso dura 24 h y no se
 *     revalida contra la base, así que quien ya estaba adentro sigue adentro.
 *     Para cortarle el acceso a alguien hay que desactivarlo, no cambiarle la
 *     clave. Si no se dice, un admin cree que echó a alguien y no lo echó.
 *  2. **La contraseña se muestra en claro** mientras se escribe. Es a propósito:
 *     el admin se la tiene que dictar a la persona, y un campo con puntitos que
 *     no se puede leer invita a poner "password123" para no equivocarse.
 */

interface ResetPasswordDialogProps {
  /** El usuario a restablecer, o `null` para tener el diálogo cerrado. */
  usuario: { id: string; nombre: string; email: string } | null;
  onClose: () => void;
}

const MINIMO = 8;

export function ResetPasswordDialog({ usuario, onClose }: ResetPasswordDialogProps) {
  const { toast } = useToast();
  const [clave, setClave] = useState('');
  const [guardando, setGuardando] = useState(false);

  const corta = clave.length > 0 && clave.length < MINIMO;

  function cerrar() {
    setClave('');
    setGuardando(false);
    onClose();
  }

  async function guardar() {
    if (!usuario || clave.length < MINIMO) return;
    setGuardando(true);
    try {
      const { warning } = await userService.resetPassword(usuario.id, clave);
      toast({
        title: 'Contraseña restablecida',
        // El aviso viene del servidor; se muestra tal cual, no se traduce ni se
        // resume, porque es el matiz que evita el malentendido.
        description:
          warning ??
          `Ya podés darle la nueva contraseña a ${usuario.nombre}. Las sesiones abiertas siguen activas.`,
      });
      cerrar();
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'No se pudo restablecer',
        // El servidor manda el motivo en `error` (p. ej. el 409 de «entra a dos empresas»).
        description: mensajeDeError(error, 'Ocurrió un error al restablecer la contraseña.'),
      });
      setGuardando(false);
    }
  }

  return (
    <Dialog open={!!usuario} onOpenChange={(abierto) => !abierto && cerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" aria-hidden="true" />
            Restablecer contraseña
          </DialogTitle>
          <DialogDescription>
            {usuario ? (
              <>
                Se le va a poner una contraseña nueva a <strong>{usuario.nombre}</strong> (
                {usuario.email}). Vos se la tenés que comunicar: el sistema no le manda ningún
                correo.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="clave-nueva">Contraseña nueva</Label>
          <Input
            id="clave-nueva"
            // En claro y no con puntitos: el admin tiene que poder dictarla.
            type="text"
            autoComplete="off"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            placeholder={`Al menos ${MINIMO} caracteres`}
            aria-invalid={corta}
            aria-describedby={corta ? 'clave-error' : undefined}
          />
          {corta && (
            <p id="clave-error" className="text-xs text-destructive">
              Tiene que tener al menos {MINIMO} caracteres.
            </p>
          )}
        </div>

        {/*
          El malentendido que este cartel evita: cambiarle la contraseña a alguien
          NO lo saca del sistema. Si el motivo es cortarle el acceso, la acción
          correcta es desactivarlo.
        */}
        <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p>
            Cambiar la contraseña <strong>no cierra las sesiones ya abiertas</strong> — siguen
            activas hasta 24 horas. Si lo que querés es cortarle el acceso, desactivá al usuario.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando || clave.length < MINIMO}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Restablecer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResetPasswordDialog;
